import { BADGE_MS, FLIP_MS, type Schedule, TICK_MS, tickSteps } from "./landing";

const FACE_DOWN = "perspective(20rem) rotateY(180deg)";
const FACE_UP = "perspective(20rem) rotateY(0deg)";
// A card's die, from nothing to its place with a turn, as a tile's flip is for a feed entry.
const DIE_POP: Keyframe[] = [
	{ transform: "scale(0.2) rotate(-200deg)", opacity: 0 },
	{ transform: "scale(1.15) rotate(8deg)", opacity: 1, offset: 0.6 },
	{ transform: "none", opacity: 1 },
];
const POP: Keyframe[] = [
	{ transform: "scale(0.6)", opacity: 0 },
	{ transform: "scale(1.15)", opacity: 1, offset: 0.6 },
	{ transform: "scale(1)", opacity: 1 },
];

let plays = 0;

// cancel() shows the final values at once, with no event: for a withdrawn
// roll, or a page going away.
export type Played = { skip(): void; cancel(): void };

// Plays a roll on a card or a feed entry: its [data-die-tile]s turn over (a card's
// [data-die]s pop in) as the schedule says, the [data-roll-total] ticks from the
// dice's sum to the total, and the [data-roll-verdict-badge], if any, pops. Each moment is a bubbling
// dice-landing:* event on the element.
export function playCard(element: HTMLElement, schedule: Schedule, { key, own }: { key: string; own: boolean }): Played {
	if (element.dataset.state === "playing") return { skip() {}, cancel() {} };
	// Two rolls can share a key (the same name, notation and result); `play` tells their events apart.
	const play = ++plays;
	const tiles = [...element.querySelectorAll<HTMLElement>("[data-die-tile]"), ...element.querySelectorAll<HTMLElement>("[data-die]")];
	const total = element.querySelector<HTMLElement>("[data-roll-total]");
	const badge = element.querySelector<HTMLElement>("[data-roll-verdict-badge]");
	const final = total?.textContent ?? "";
	const modifier = Number(element.querySelector("[data-roll-modifier]")?.textContent.trim() || 0) || 0;
	const sum = Number(final.trim()) - modifier;
	const steps = Number.isFinite(sum) ? tickSteps(sum, modifier) : [];
	const timers: ReturnType<typeof setTimeout>[] = [];
	const animations: Animation[] = [];
	let playing = true;

	const emit = (name: string, detail: Record<string, unknown> = {}) =>
		element.dispatchEvent(new CustomEvent(`dice-landing:${name}`, { bubbles: true, detail: { key, own, play, ...detail } }));
	const later = (at: number, run: () => void) => timers.push(setTimeout(run, at));
	const settle = () => {
		if (total) {
			if (total.textContent !== final) total.textContent = final;
			total.style.opacity = "";
		}
		if (badge) badge.style.opacity = "";
		element.dataset.state = "done";
	};
	const end = () => {
		playing = false;
		emit("done");
	};

	if (schedule.mode === "instant" || typeof element.animate !== "function") {
		settle();
		emit("start", { mode: "instant", dice: tiles.length });
		end();
		return { skip() {}, cancel() {} };
	}

	element.dataset.state = "playing";
	if (total) {
		total.style.opacity = "0";
		total.style.fontVariantNumeric = "tabular-nums";
		total.style.minWidth = `${Math.max(final.trim().length, ...steps.map((step) => String(step).length))}ch`;
	}
	if (badge) badge.style.opacity = "0";
	emit("start", { mode: schedule.mode, dice: tiles.length });

	tiles.forEach((tile, index) => {
		const at = schedule.revealAt[index] ?? schedule.revealAt.at(-1) ?? 0;
		const die = tile.dataset.die !== undefined;
		animations.push(
			tile.animate(die ? DIE_POP : [{ transform: FACE_DOWN }, { transform: FACE_UP }], {
				duration: FLIP_MS,
				delay: Math.max(0, at - FLIP_MS),
				easing: die ? "cubic-bezier(0.3, 1.4, 0.5, 1)" : "ease-out",
				fill: "backwards",
			}),
		);
		later(at, () => {
			const value = die ? tile.dataset.value : tile.querySelector("[data-die-value]")?.textContent.trim();
			emit("flip", { index, sides: Number(tile.dataset.sides), value: Number(value) });
		});
	});

	const totalAt = steps.length ? schedule.tickAt + TICK_MS : schedule.tickAt;
	if (total) {
		later(schedule.tickAt, () => {
			total.style.opacity = "";
			total.textContent = String(steps[0] ?? final);
		});
		steps.slice(1, -1).forEach((step, k) => {
			later(schedule.tickAt + (TICK_MS * (k + 1)) / (steps.length - 1), () => {
				total.textContent = String(step);
			});
		});
	}
	later(totalAt, () => {
		if (total && total.textContent !== final) total.textContent = final;
		emit("total", { value: Number(final.trim()) });
		if (badge) {
			badge.style.opacity = "";
			animations.push(badge.animate(POP, { duration: BADGE_MS, easing: "ease-out" }));
		}
	});
	later(Math.max(schedule.doneAt, totalAt + (badge ? BADGE_MS : 0)), () => {
		settle();
		end();
	});

	const stop = (finish: boolean) => {
		playing = false;
		for (const timer of timers) clearTimeout(timer);
		for (const animation of animations) {
			try {
				if (finish) animation.finish();
				else animation.cancel();
			} catch {
				animation.cancel();
			}
		}
	};

	return {
		skip() {
			if (!playing) return;
			stop(true);
			settle();
			emit("skip");
			emit("done");
		},
		cancel() {
			if (!playing) return;
			stop(false);
			settle();
		},
	};
}
